/**
 * Bridges inbox-thread document-viewer toolbar <-> InboxReader actions.
 * Toolbar owns the single chrome row; reader registers Discuss / overflow handlers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Status } from './types'

export type InboxThreadChromeActions = {
  onReply: () => void
  /** Soul proposals keep a Discuss button. Other threads use the footer + menu. */
  primaryActionLabel?: 'Discuss'
  threadId: string
  threadTitle?: string
  threadStatus: Status
  linkedConversationId: string | null
  onSnooze: (id: string, label: string, hint: string) => void
  onArchive: (id: string) => void
  onMarkAsUnread?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  onOpenInChat?: (id: string) => void
  onOpenInNewTab?: (id: string) => void
  /** When false, hide overflow (e.g. archived) */
  showThreadActions: boolean
}

type InboxThreadChromeContextValue = {
  actions: InboxThreadChromeActions | null
  setActions: (next: InboxThreadChromeActions | null) => void
}

const InboxThreadChromeContext = createContext<InboxThreadChromeContextValue | null>(null)

export function InboxThreadChromeProvider({ children }: { children: ReactNode }) {
  const [actions, setActionsState] = useState<InboxThreadChromeActions | null>(null)
  const setActions = useCallback((next: InboxThreadChromeActions | null) => {
    setActionsState(next)
  }, [])
  const value = useMemo(() => ({ actions, setActions }), [actions, setActions])
  return (
    <InboxThreadChromeContext.Provider value={value}>
      {children}
    </InboxThreadChromeContext.Provider>
  )
}

export function useInboxThreadChromeActions() {
  return useContext(InboxThreadChromeContext)?.actions ?? null
}

/** Register toolbar actions while an embedded reader is mounted. */
export function useRegisterInboxThreadChromeActions(
  actions: InboxThreadChromeActions | null,
  enabled: boolean,
) {
  const ctx = useContext(InboxThreadChromeContext)
  useEffect(() => {
    if (!ctx) return
    if (!enabled || !actions) {
      ctx.setActions(null)
      return
    }
    ctx.setActions(actions)
    return () => ctx.setActions(null)
  }, [
    ctx,
    enabled,
    actions,
  ])
}
